import * as React from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { Avatar, Chip, CircularProgress } from '@mui/material'
import { getArrayDifferenceById, stringAvatar } from '@assembly/utils'
import { RoleLabelMapping, TeamMember, Roles } from '@assembly/pages/Teams/types'
import { getOrganizationMembers } from '@assembly/api/organization'
import { getGlobalTeamMembersByRoles } from '@assembly/api/teamMember'

type OrgMembersSearchAndSelectProps = {
  orgId: string
  onChange: (members: TeamMember[]) => void
  value: TeamMember[]
  error?: boolean
  helperText?: string
}

export default function OrgMembersSearchAndSelect({
  onChange,
  value,
  error,
  helperText,
  orgId,
}: OrgMembersSearchAndSelectProps) {
  const [options, setOptions] = React.useState<TeamMember[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)

  const getOrgMembersAndGlobalAdminsAsync = React.useCallback(async () => {
    try {
      setLoading(true)

      // Define admin roles to search for globally
      const adminRoles = [
        Roles.InternalAdmin,
        Roles.SuperAdmin,
        Roles.OrchestrationAdmin
      ]

      // Fetch both organization members and global admin members in parallel
      const [orgMembersResponse, globalAdminsResponse] = await Promise.all([
        getOrganizationMembers(orgId),
        getGlobalTeamMembersByRoles(adminRoles)
      ])

      const orgMembers = orgMembersResponse.data || []
      const globalAdmins = globalAdminsResponse.data || []

      // Combine and deduplicate members based on email
      const allMembers = [...orgMembers, ...globalAdmins]
      const uniqueMembers = allMembers.reduce((acc: TeamMember[], current: TeamMember) => {
        const existingMember = acc.find(member => member.email === current.email)
        if (!existingMember) {
          acc.push(current)
        }
        return acc
      }, [])

      const combinedMembers = uniqueMembers

      setOptions(combinedMembers)
      setLoading(false)
    } catch (error) {
      console.error(error)
      setLoading(false)
    }
  }, [orgId])

  React.useEffect(() => {
    if (orgId) {
      getOrgMembersAndGlobalAdminsAsync()
    }
  }, [orgId, getOrgMembersAndGlobalAdminsAsync])

  return (
    <Autocomplete
      multiple
      loading={loading}
      getOptionLabel={(option) => option.name}
      options={getArrayDifferenceById(options, value)}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText="No members"
      onChange={(_: any, newValue: any) => {
        onChange(newValue)
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Add Members"
          fullWidth
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(props, option: TeamMember) => {
        const isOrgMember = option.organizationID === orgId
        return (
          <li {...props} key={option.id}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <Avatar
                  sx={{
                    width: 35,
                    height: 35,
                    bgcolor: stringAvatar(option.name || option.email).sx
                      .bgcolor,
                  }}
                />
              </Grid>
              <Grid
                item
                sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}
              >
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  {option.name || option.email}
                  {!isOrgMember && (
                    <>
                      <Chip
                        label="External"
                        size="small"
                        color="warning"
                        sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                      />
                      <Chip
                        label={RoleLabelMapping[option.role]}
                        size="small"
                        color="secondary"
                        sx={{ ml: 0.5, fontSize: '0.7rem', height: '20px' }}
                      />
                    </>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {RoleLabelMapping[option.role]}
                </Typography>
              </Grid>
            </Grid>
          </li>
        )
      }}
      renderTags={(value: any, getTagProps) =>
        value.map((option: any, index: number) => (
          <Chip
            avatar={<Avatar />}
            color="primary"
            label={option.name || option.email}
            {...getTagProps({ index })}
          />
        ))
      }
    />
  )
}
